import { clsx } from 'clsx';
import { Check, ChevronDown, Search } from 'lucide-react';
import type {
  FieldsetHTMLAttributes,
  HTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  Ref,
  SelectHTMLAttributes,
} from 'react';

/*
 * List toolbars: one row of pill controls above a table (search, filter selects, toggles), then the active-filter
 * chips. Every control is the native element with a real <label>, so keyboard, form and test semantics stay as they
 * are; only the chrome is a 36px pill that matches `Segmented` and `Button`.
 */

const PILL =
  'inline-flex h-[36px] items-center rounded-full border border-line-strong bg-surface text-[13.5px] transition-[border-color,box-shadow] duration-150 hover:border-control-border focus-within:border-control-border-hover';

/** The toolbar row. A `fieldset` (legend hidden) so the filters are announced as one group. */
export function FilterBar({
  legend = 'Filters',
  className,
  children,
  ...rest
}: FieldsetHTMLAttributes<HTMLFieldSetElement> & { legend?: string }) {
  return (
    <fieldset
      className={clsx('m-0 flex min-w-0 flex-wrap items-center gap-2 border-0 p-0', className)}
      {...rest}
    >
      <legend className="sr-only">{legend}</legend>
      {children}
    </fieldset>
  );
}

export interface SearchInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  id: string;
  /** Accessible name; shown as the placeholder unless one is given. */
  label: string;
  ref?: Ref<HTMLInputElement>;
}

/** Pill search field with a leading icon. The label is visually hidden; the placeholder says what it searches. */
export function SearchInput({ id, label, className, placeholder, ref, ...rest }: SearchInputProps) {
  return (
    <div className={clsx('relative min-w-0', className)}>
      <label htmlFor={id} className="sr-only">
        {label}
      </label>
      <Search
        aria-hidden
        className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-ink-3"
        strokeWidth={1.75}
      />
      <input
        ref={ref}
        id={id}
        type="search"
        placeholder={placeholder ?? label}
        className={clsx(
          PILL,
          'w-full pr-4 pl-10 text-ink placeholder:text-placeholder focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
        )}
        {...rest}
      />
    </div>
  );
}

export interface FilterSelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  id: string;
  /** Visible prefix inside the pill ("State"); also the accessible name of the select. */
  label: string;
  children: ReactNode;
  ref?: Ref<HTMLSelectElement>;
}

/**
 * "State: All states ▾" pill. The label text stays exactly `label` (the colon is drawn by CSS), so
 * `getByLabelText('State')` keeps working.
 */
export function FilterSelect({ id, label, className, children, ref, ...rest }: FilterSelectProps) {
  return (
    <span className={clsx(PILL, 'relative max-w-full gap-1 pl-3.5', className)}>
      <label htmlFor={id} className="shrink-0 cursor-pointer text-ink-3 after:content-[':']">
        {label}
      </label>
      <select
        ref={ref}
        id={id}
        className="h-full min-w-0 flex-1 cursor-pointer appearance-none truncate rounded-full border-0 bg-transparent pr-8 pl-0.5 font-semibold text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
        {...rest}
      >
        {children}
      </select>
      <ChevronDown
        aria-hidden
        className="pointer-events-none absolute top-1/2 right-3 size-3.5 -translate-y-1/2 text-ink-2"
        strokeWidth={2}
      />
    </span>
  );
}

export interface FilterToggleProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  id: string;
  label: string;
  ref?: Ref<HTMLInputElement>;
}

/** On/off filter as a pill ("Requested by me"): a native checkbox; the pill fills with the accent tint when on. */
export function FilterToggle({ id, label, className, ref, ...rest }: FilterToggleProps) {
  return (
    <label
      htmlFor={id}
      className={clsx(
        PILL,
        'cursor-pointer gap-2 px-3.5 text-ink-2 has-[:checked]:border-transparent has-[:checked]:bg-accent-soft has-[:checked]:text-accent-ink has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-focus',
        className,
      )}
    >
      <input ref={ref} id={id} type="checkbox" className="peer sr-only" {...rest} />
      <span
        aria-hidden
        className="inline-flex size-4 items-center justify-center rounded-full border border-control-border bg-surface text-on-accent peer-checked:border-accent peer-checked:bg-accent [&>svg]:opacity-0 peer-checked:[&>svg]:opacity-100"
      >
        <Check className="size-3" strokeWidth={3.5} />
      </span>
      <span className="font-medium">{label}</span>
    </label>
  );
}

/** One active filter, shown under the toolbar ("State: Running"). */
export function FilterChip({ className, ...rest }: HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      className={clsx(
        'inline-flex h-7 items-center rounded-full bg-surface-2 px-3 text-[12.5px] font-medium text-ink-2',
        className,
      )}
      {...rest}
    />
  );
}
